import React from 'react';
import { EventPickerProps, UseEventPickerControllerReturn } from './EventPicker.controller';
import { Container, Row, PhotoBox, StyledCaption, CreateNewButton } from './EventPicker.styles';
import { addRendition } from '@shared/utils/photoRendition';
import { TextV1, Box } from '@withjoy/joykit';
import { ReactComponent as CircleArrow } from '@assets/icons/circle-arrow-right.svg';
import { capitalizeWord } from '@shared/utils/strings';
import ClampedText from '../ClampedText';
import { ReactComponent as AddIcon } from '@assets/icons/circle-plus.svg';
import { useTranslation } from '@shared/core';
import { EventType, EventUserStatus, useGetEventPhotoByEventHandleQuery } from '@graphql/generated';

type EventEntryProps = {
  handleOnEventClick: UseEventPickerControllerReturn['handleOnEventClick'];
  eventId: string;
  firebaseId: string;
  website: string;
  eventMonogram?: string | null;
  eventDisplayName?: string | null;
  date?: string;
  location?: string | null;
  eventStatus: EventUserStatus;
  currentEventId?: string;
  eventType?: Maybe<EventType>;
};

const EventPhoto: React.FC<{ eventId: string; website: string; eventMonogram: Maybe<string> }> = ({ eventId, website, eventMonogram }) => {
  const { data } = useGetEventPhotoByEventHandleQuery({
    variables: {
      website,
    },
    batchMode: 'fast',
  });

  const photoUrl = addRendition({ url: data?.eventByName?.photo?.url, renditionSize: 'small' });
  return (
    <Box
      width="100%"
      height="100%"
      backgroundRepeat="no-repeat"
      backgroundSize="cover"
      borderRadius="45px"
      backgroundPosition="center center"
      backgroundColor={'mono3'}
      display="flex"
      transition="all 0.5s"
      alignItems="center"
      justifyContent="center"
      backgroundImage={photoUrl ? `url(${photoUrl})` : undefined}
    >
      {!photoUrl && (
        <TextV1
          fontFamily="'Open Sans', sans-serif"
          fontSize="17px"
          fontStyle="normal"
          fontWeight={600}
          lineHeight="26px"
          letterSpacing="0.2px"
          color={'mono10'}
        >
          {eventMonogram}
        </TextV1>
      )}
    </Box>
  );
};

const EventEntry = React.memo((props: EventEntryProps) => {
  const { eventId, website, eventMonogram, eventDisplayName, date, eventType, firebaseId, location, handleOnEventClick, eventStatus, currentEventId } = props;
  const { t2 } = useTranslation('account');
  const { currentEvent } = t2('eventPicker');

  return (
    <Row
      currentEvent={currentEventId === eventId}
      alignItems="center"
      justifyContent="space-between"
      display="flex"
      width="100%"
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        if (eventId && website) {
          handleOnEventClick(eventId, firebaseId, website, eventStatus, eventType);
        }
      }}
    >
      <Box display="flex">
        <PhotoBox
          currentEvent={currentEventId === eventId}
          overflow="hidden"
          height="90px"
          width="90px"
          borderRadius="45px"
          marginRight={6}
        >
          <EventPhoto
            eventId={eventId}
            website={website}
            eventMonogram={eventMonogram}
          />
        </PhotoBox>
        <Box maxWidth="215px">
          <TextV1
            textAlign="left"
            fontSize="17px"
            fontWeight={600}
            letterSpacing="-1%"
            color={'mono14'}
            component="p"
            variant="hed2"
            lineHeight="24px"
          >
            {eventDisplayName}
          </TextV1>
          <TextV1
            variant="label2"
            textAlign="left"
            color={'typeTertiaryDark'}
            component="p"
          >
            {date}
          </TextV1>
          <ClampedText
            clampAt={1}
            disableTooltip={true}
            typographyVariant="label2"
            textAlign="left"
            color={'typeTertiaryDark'}
            component="p"
            marginBottom={3}
          >
            {location}
          </ClampedText>
          <Box
            display="flex"
            alignItems="center"
          >
            {currentEventId === eventId && (
              <Box
                padding="4px"
                borderRadius="4px"
                backgroundColor={'typeTertiaryDark'}
                marginRight={3}
              >
                <StyledCaption
                  textAlign="center"
                  variant="label2"
                  component="p"
                  lineHeight="18.2px"
                >
                  {currentEvent}
                </StyledCaption>
              </Box>
            )}
            <TextV1
              variant="label2"
              textAlign="left"
              color={'typeTertiaryDark'}
              component="p"
            >
              {capitalizeWord(eventStatus || '')}
            </TextV1>
          </Box>
        </Box>
      </Box>
      <Box>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <CircleArrow />
        </Box>
      </Box>
    </Row>
  );
});

export const EventPicker: React.FC<Pick<EventPickerProps, 'currentEventId' | 'onCreateNewEvent'> & UseEventPickerControllerReturn> = props => {
  const { currentEventId, handleOnEventClick, eventMemberships, formatDate, onCreateNewEvent } = props;
  const { t2 } = useTranslation('account');
  const { createNew } = t2('eventPicker');

  return (
    <Box>
      <Container
        display="flex"
        flexDirection="column"
      >
        {eventMemberships.map(eventMembership => {
          if (!eventMembership.event) {
            return null;
          }
          const location = eventMembership.event.info?.location;
          const date = formatDate(eventMembership.event.info.finalizedDate);
          const eventDisplayName = eventMembership.event.info.eventDisplayName;
          const eventMonogram = eventMembership.event.info.eventMonogram;
          const website = eventMembership.event.website;
          const eventId = eventMembership.event.id;
          const firebaseId = eventMembership.event.firebaseId;
          const eventType = eventMembership.event.info?.eventType;
          const eventStatus = eventMembership.status;
          return (
            <EventEntry
              key={eventId}
              handleOnEventClick={handleOnEventClick}
              date={date}
              location={location}
              firebaseId={firebaseId}
              eventDisplayName={eventDisplayName}
              eventMonogram={eventMonogram}
              eventId={eventId}
              eventType={eventType}
              website={website}
              eventStatus={eventStatus}
              currentEventId={currentEventId}
            />
          );
        })}
      </Container>
      <CreateNewButton
        onClick={() => {
          onCreateNewEvent('event picker dialog');
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          marginRight={'9px'}
        >
          <AddIcon />
        </Box>
        {createNew}
      </CreateNewButton>
    </Box>
  );
};
